import { SearchProductFilter, SubSection } from '@teko/discovery/v2';
import { omit } from '@teko/ds-desktop';
import { Filter } from '@teko/types/ecommerce';
import { stringToInteger } from '@teko/utils';
import { t } from 'i18n';
import { ParsedUrlQuery, ParsedUrlQueryInput } from 'querystring';
import { ObjectMap, OrderBy } from 'types';
import { SubSectionBlock } from 'types/config';
import { CategoryNode, CategoryTree, SortOptionInterface } from './types';

/**
 *
 * @param  query
 * @returns
 * remove dynamic params from query
 * - ex: domain.vn/c/abc?slug=abc&page=2 -> domain.vn/c/abc?page=2
 */
export const omitQuery = (query: ObjectMap) => {
  const omitKeys = [
    'slug',
    'categoryId',
    'sellerCategoryId',
    'brandId',
    'sellerId',
  ];
  return omit(query, omitKeys);
};

const joinSortQuery = ({ sort, order }: { sort: string; order: string }) => {
  return `${sort}${SORT_QUERY_JOIN_CHAR}${order}`;
};
const SORT_QUERY_JOIN_CHAR = '&';

export const getDefaultSortOptions = (
  sortByCreatedAt?: boolean,
): SortOptionInterface[] => {
  return [
    {
      optionName: t('Khuyến mãi tốt nhất'),
      optionCode: joinSortQuery({
        sort: 'SORT_BY_DISCOUNT_PERCENT',
        order: OrderBy.DESC,
      }),
    },
    {
      optionName: t('Giá tăng dần'),
      optionCode: joinSortQuery({ sort: 'SORT_BY_PRICE', order: OrderBy.ASC }),
    },
    {
      optionName: t('Giá giảm dần'),
      optionCode: joinSortQuery({ sort: 'SORT_BY_PRICE', order: OrderBy.DESC }),
    },
    {
      optionName: t('Sản phẩm mới nhất'),
      optionCode: joinSortQuery({
        sort: !!sortByCreatedAt ? 'SORT_BY_CREATED_AT' : 'SORT_BY_PUBLISH_AT',
        order: OrderBy.DESC,
      }),
    },
    {
      optionName: t('Sản phẩm bán chạy nhất'),
      optionCode: joinSortQuery({
        sort: 'SORT_BY_TOP_SALE_QUANTITY_7_DAYS',
        order: OrderBy.DESC,
      }),
    },
  ];
};

export const extractSortQuery = (joinedQuery: string) => {
  return joinedQuery.split(SORT_QUERY_JOIN_CHAR);
};

export const getActiveSortOption = (
  query: ParsedUrlQueryInput,
  sortByCreatedAt?: boolean,
  defaultSortOptionCode?: string,
) => {
  const defaultSortOption = defaultSortOptionCode
    ? getDefaultSortOptions(sortByCreatedAt).find(
        (option) => option.optionCode === t(defaultSortOptionCode),
      ) || null
    : null;
  const sortOptionFromQuery = getDefaultSortOptions(sortByCreatedAt).find(
    (opt) =>
      opt.optionCode ===
      joinSortQuery({
        sort: query.sort as string,
        order: query.order as string,
      }),
  );
  return sortOptionFromQuery ?? defaultSortOption;
};

export const clearFilterLink = (rootPath: string, query: ParsedUrlQuery) => {
  return rootPath === '/search'
    ? `/search?router=productListing${
        query.query ? `&query=${query.query}` : ''
      }`
    : `${
        typeof window !== 'undefined' ? window.location.origin : ''
      }${rootPath}`;
};

export const cookFilterPrice = (lte = 10000000, gte = 0) => {
  /**
   * default : gte: 0, lte: 10.000.000, step = 500.000, delta = lte - gte
   * lte: is rounded to 50k, 500K, 1M, 5M
   * step = (delta / 20) is rounded to 5k, 10k, 100K, 500K
   */
  lte = lte === null || isNaN(lte) ? 10000000 : lte;
  gte = gte === null || isNaN(gte) ? 0 : gte;
  let priceLte =
    stringToInteger(lte.toString()) ||
    (stringToInteger(gte.toString(), 0) as number) + 500000;
  const priceGte = 0;

  if (priceLte < 200000) {
    priceLte = Math.ceil(priceLte / 50000) * 50000;
  }

  if (priceLte > 200000 && priceLte < 500000) {
    priceLte = 500000;
  }

  if (priceLte > 500000 && priceLte < 5000000) {
    priceLte = Math.ceil(priceLte / 1000000) * 1000000;
  }
  if (priceLte > 5000000) {
    priceLte = Math.ceil(priceLte / 5000000) * 5000000;
  }

  const delta = priceLte - priceGte;

  let step = 100000;

  if (delta < 200000) {
    step = Math.ceil(delta / 100000) * 5000;
    /**minStep : 5.000d , maxStep: 10.000 */
  }
  if (delta > 200000 && delta <= 2000000) {
    step = Math.ceil(delta / 200000) * 10000;
    /**minStep : 20.000d , maxStep: 100.000 */
  }

  if (delta > 2000000 && delta <= 10000000) {
    step = Math.ceil(delta / 2000000) * 100000;
    /**minStep : 100.000d , maxStep: 500.000 */
  }
  if (delta > 10000000) {
    step = 500000;
  }

  // steps

  return {
    max: priceLte,
    min: priceGte,
    step: step,
  };
};

export const getDisplayFilter = (filters: Filter[]) => {
  return filters.filter(
    (filter) => filter?.options?.length && filter.options.length > 1,
  );
};
export const getCollectionSlug = (slug: string) => (slug ? `/c/${slug}` : '');

export const getSignatureQueryParam = (query: ParsedUrlQuery) => {
  let signatureFilterQueryParams: ObjectMap = {};
  const {
    brandId,
    sellerId,
    providerId,
    sellerCategories,
    masterCategoryIds,
    categoryId,
    sellerCategoryId,
  } = query;
  // handle link like this: /search?router=productListing&sellerCategories=015
  if (sellerCategories) {
    signatureFilterQueryParams = {
      masterCategoryIds: sellerCategories,
    };
  }
  // handle link like this: /search?router=productListing&masterCategoryIds=3983,3973,3948&_sort=discountPercent&_order=desc
  else if (masterCategoryIds) {
    signatureFilterQueryParams = {
      masterCategoryIds: masterCategoryIds,
    };
  } else if (providerId) {
    signatureFilterQueryParams = {
      providerIds: [providerId],
    };
  } else if (sellerId) {
    signatureFilterQueryParams = {
      sellerIds: [sellerId],
    };
  } else if (brandId) {
    signatureFilterQueryParams = {
      brands: typeof brandId === 'string' ? [brandId] : [...brandId],
    };
  } else if (categoryId) {
    signatureFilterQueryParams = {
      masterCategoryIds: [categoryId],
    };
  } else if (sellerCategoryId) {
    signatureFilterQueryParams = {
      categories: [sellerCategoryId],
    };
  }
  return signatureFilterQueryParams;
};

/**
 *
 * @param i a number
 * @returns return '01' if i = 1, return '11' if i = 11
 */
const padString = (i: number) => (i < 10 ? '0' + i : '' + i);
const CURRENT_MONTH_KEY = '%currentMonth%';
const CURRENT_YEAR_KEY = '%currentYear%';
export const replaceVariablesWithValues = (date: Date, metaTitle?: string) => {
  const mapMetaTitleVariables: { [key: string]: string } = {
    [CURRENT_MONTH_KEY]: padString(date.getMonth() + 1),
    [CURRENT_YEAR_KEY]: padString(date.getFullYear()),
  };
  if (metaTitle) {
    const reg = new RegExp(Object.keys(mapMetaTitleVariables).join('|'), 'g');
    return metaTitle.replace(reg, (matched) => mapMetaTitleVariables[matched]);
  }
  return metaTitle;
};

export const cookCategoryTree = (
  rawCategories: SearchProductFilter['categories'],
): {
  cookedCategories: CategoryTree;
  cookedCategoriesById: Map<number, CategoryNode>;
  cookedCategoriesByCode: Map<string, CategoryNode>;
} => {
  if (!rawCategories?.length)
    return {
      cookedCategories: {},
      cookedCategoriesById: new Map(),
      cookedCategoriesByCode: new Map(),
    };

  const nodeMap = new Map<number, CategoryNode>();
  for (const c of rawCategories) {
    nodeMap.set(c.id, { ...c, children: {} });
  }

  const result: CategoryTree = {};
  const nodeById = new Map<number, CategoryNode>();
  const nodeByCode = new Map<string, CategoryNode>();
  for (const node of nodeMap.values()) {
    const parentId = node.parentId ?? 0;
    const parent = parentId ? nodeMap.get(parentId) : undefined;

    if (!parent) {
      result[node.id] = node;
    } else {
      parent.children[node.id] = node;
    }
    nodeById.set(node.id, node);
    nodeByCode.set(node.code, node);
  }

  return {
    cookedCategories: result,
    cookedCategoriesById: nodeById,
    cookedCategoriesByCode: nodeByCode,
  };
};

export const getKeyFromNode = (node: CategoryNode) => {
  return `${node.id}_${node.parentId ?? 0}`;
};

export const getDataFromKey = (key: string) => {
  const [id, parentId] = key.split('_');
  return { id: Number(id), parentId: Number(parentId) };
};

export const findChildren = (node: CategoryNode, skipInitial = false) => {
  const keys: string[] = [];
  !skipInitial && node?.id && keys.push(getKeyFromNode(node));
  for (const child of Object.values(node.children ?? {})) {
    keys.push(...findChildren(child));
  }
  return keys;
};

export const findAncestors = (
  node: CategoryNode,
  cookedCategoriesById: Map<number, CategoryNode>,
  skipInitial = false,
) => {
  const ancestorsKeys: string[] = [];
  !skipInitial && node?.id && ancestorsKeys.push(getKeyFromNode(node));
  let parentId = node?.parentId;
  while (parentId) {
    const parent = cookedCategoriesById.get(parentId);
    if (!parent) break;
    ancestorsKeys.push(getKeyFromNode(parent));
    parentId = parent.parentId;
  }
  return ancestorsKeys;
};

export const convertSubsections = (
  subsectionsFromWorkload: SubSectionBlock,
) => {
  const { brands, others } = subsectionsFromWorkload;
  const res: SubSection[] = [];
  if (!!brands && !!brands.length) {
    res.push({
      type: 'IMAGES',
      items: brands.map((brand) => ({
        linkUrl: brand.url,
        thumbnailUrl: brand.logo,
      })),
    });
  }
  others?.forEach((other) => {
    if (!!other?.items.length) {
      res.push({
        type: 'IMAGES_AND_TEXT',
        title: other.label,
        items: other.items.map((item) => ({
          linkUrl: item.url,
          thumbnailUrl: item.image,
          description: item.description,
        })),
      });
    }
  });
  return res;
};
